<template>
  <div class="container text m-b-2-md">
    <h1 class="titulo m-t-1-sm m-t-3-md">¿Qué es el VIH y cómo se replica?</h1>

    <div class="a11y-simplificada-block">
      <a href="https://www.youtube.com/embed/TM4Bf_fssnY" target="_blank" rel="noopener noreferrer">
        Proyecto Conahcyt - Estudio de vigilancia molecular del VIH
      </a>
    </div>

    <div class="video-responsivo ocultar-grafica">
      <iframe
        width="560" 
        height="315" 
        src="https://www.youtube.com/embed/TM4Bf_fssnY" 
        title="YouTube video player" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen>
      </iframe>
    </div>

    <div class="contenido">
      <p>
        El Virus de Inmunodeficiencia Humana (VIH), es una
        <strong
          >partícula diminuta que consiste de material genético (RNA) y enzimas,
          rodeados por una estructura llamada cápside formada por proteínas y
          una envoltura lipídica</strong
        >. Para que el VIH pueda realizar más copias de sí mismo (replicarse)
        infecta a las células del cuerpo, principalmente a los “linfocitos T CD4
        +” que son un grupo de células del sistema inmune. Una vez dentro de las
        células, el VIH se une a un sitio específico de ellas, de forma similar
        a una llave en una cerradura. Cuando ocurre la unión, la envoltura del
        VIH se fusiona con la membrana celular, liberando su cápside en el
        citoplasma de la célula. Posteriormente, la cápside se desensambla y
        comienza la conversión del RNA a DNA, mediante la enzima transcriptasa
        reversa (RT), es por ello que, a este proceso se le conoce como
        transcripción inversa o retrotranscripción. El DNA viral resultante es
        transportado hacia el núcleo de la célula, donde se inserta al genoma
        celular a través de la acción de la enzima viral integrasa (IN). Ya
        insertado en el genoma celular, el VIH utiliza los procesos celulares
        para replicar su genoma y producir poliproteínas (muchas proteínas
        unidas). Ahora, las partes del virus (genoma y poliproteínas) están
        listas para ser ensambladas, al liberarse de la célula se lleva parte de
        la membrana celular que formará la nueva envoltura viral.
      </p>
      <p>
        Las partículas virales liberadas requieren alcanzar un estado maduro
        para poder ser infecciosas y capaces de infectar nuevas células. Este
        estado de madurez es alcanzado gracias a la acción de la enzima viral
        proteasa (PR) que corta las poliproteínas para que estas adquieran su
        estructura y función. Los diversos mecanismos que ocurren durante la
        replicación del VIH ocasionan que las células hospederas se debiliten o
        mueran, disminuyendo su capacidad para proteger el cuerpo de otras
        infecciones.
      </p>
      <p>
        <strong
          >La replicación del VIH es un proceso que ocurre a gran
          velocidad</strong
        >, en el cual existen muchos eventos de recombinación. Además,
        <strong
          >al convertir el RNA viral en DNA, la enzima retrotranscriptasa comete
          muchos errores, lo que facilita el surgimiento de tipos ligeramente
          diferentes de VIH, llamados “variantes”</strong
        >. Estas nuevas variantes de VIH podrían tener la capacidad de evadir
        los mecanismos de defensa de la persona infectada. Si la infección por
        VIH no es tratada y el sistema inmune no es capaz de disminuir la
        velocidad de multiplicación del virus, aumenta la probabilidad de que se
        desarrolle el Síndrome de Inmunodeficiencia Adquirida (SIDA), que es una
        enfermedad sintomática.
      </p>
      <h2 class="subtitulo m-t-1-sm m-t-1-md">
        ¿Existe cura para el VIH?, ¿qué son los antirretrovirales? y ¿qué es la
        resistencia?
      </h2>
      <p>
        A pesar de que actualmente no existe cura para
        <strong>la enfermedad causada por el VIH</strong>, esta
        <strong
          >puede controlarse usando medicamentos especiales llamados
          antirretrovirales</strong
        >. Los antirretrovirales son
        <strong>medicamentos que evitan la replicación del VIH</strong>, sus
        principales objetivos son las enzimas transcriptasa reversa, integrasa y
        proteasa, originadas por la expresión del gen <em>pol</em>.
      </p>
      <p>
        <strong>Hay varias clases de antirretrovirales</strong>, se clasifican
        dependiendo de en qué punto del proceso de replicación del VIH actúan:
        1) los <strong>inhibidores de entrada</strong>, bloquean la unión o
        fusión del virus a los linfocitos CD4 T +, 2) los
        <strong
          >inhibidores análogos de nucleósidos de la retrotranscriptasa
          (NRTI)</strong
        >
        evitan que el RNA sea convertido en DNA, al promover la terminación
        prematura de la cadena de DNA, 3) los
        <strong
          >inhibidores no análogos de nucleósidos de la retrotranscriptasa
          (NNRTI)</strong
        >
        también detienen la replicación del DNA durante la retrotranscripción
        modificando la estructura de la enzima, 4) los
        <strong>inhibidores de integrasa (INI)</strong> impiden que el DNA del
        virus se integre al material genético de la célula, y finalmente 5) los
        <strong>inhibidores de proteasa (PI)</strong> evitan que el virus
        alcance un estado maduro.
      </p>
      <p>
        <strong
          >El tratamiento antirretroviral (TARV) actual consiste en la
          combinación de fármacos de al menos dos clases</strong
        >, por ejemplo: 2 NRTI+1 INI. Los objetivos principales del TARV es
        evitar la replicación del VIH y evitar el deterioro del sistema de
        defensa de las personas con VIH. Es por ello que, es altamente
        recomendable que TODAS las personas al ser diagnosticadas con VIH
        reciban TARV tan pronto como sea. Sin embargo,
        <strong
          >el uso prolongado del TARV sin una buena supervisión, puede generar
          resistencia del VIH a estos medicamentos</strong
        >. Notablemente, la resistencia puede ser transmitida a personas de
        reciente infección que no han recibido TARV.
      </p>

      <h2 class="subtitulo m-t-1-sm m-t-1-md">
        ¿Cómo se estudia el desarrollo de la infección por VIH?
      </h2>
      <p>
        <strong
          >Para vigilar el desarrollo de la infección por VIH antes y después de
          que la persona empiece a tomar TARV, existen dos estudios de
          laboratorio</strong
        >: 1) la carga viral de VIH en plasma y 2) el conteo de linfocitos T CD4
        + en sangre. La
        <strong
          >carga viral es el número de virus circulando en la sangre de una
          persona con VIH</strong
        >, cuando las personas con VIH tienen una carga viral más alta pueden
        transmitir el virus con mayor facilidad. Mientras que,
        <strong
          >el conteo de linfocitos T CD4 + en la sangre permite determinar qué
          tan deteriorado está el sistema inmune</strong
        >
        (sistema de defensas) de una persona que tiene VIH. El número normal de
        linfocitos T CD4 + en una persona que no tiene VIH es de alrededor de
        500 a 1400 células/mm3 de sangre. Cuando el conteo de linfocitos T CD4 +
        es menor de 200 células/mm3 en una persona con VIH, se dice que está en
        etapa de SIDA. Una vez que una persona con VIH empieza a tomar TARV es
        deseable que la carga viral del VIH se haga indetectable, es decir que
        sea menor a 50 virus/mL de plasma y que el conteo de linfocitos T CD4+
        empiece a aumentar. Si esto ocurre, entonces el tratamiento está siendo
        exitoso. Además, si la carga viral es indetectable disminuye la
        probabilidad de que la persona con VIH transmita el virus.
      </p>
      <h2 class="subtitulo m-t-1-sm m-t-1-md">
        ¿Qué es la secuenciación genómica del VIH y para qué sirve?
      </h2>
      <p>
        Una forma de estudiar el VIH y prevenir la resistencia al TARV es
        mediante la secuenciación genómica, que
        <strong
          >es un método de laboratorio a través del cual es posible determinar
          la composición genética completa de un organismo</strong
        >. La secuenciación genómica del VIH permite detectar variantes del
        virus, conocer su origen, conocer su capacidad para causar enfermedad,
        encontrar y predecir la resistencia a los antirretrovirales, generar
        información para el desarrollo de tratamientos y vacunas en el futuro,
        así como vigilar la transmisión del virus.
        <strong
          >La vigilancia hace posible construir una “red de transmisión del VIH”
          e identificar grupos de transmisión (conglomerados)</strong
        >, lo que en última instancia ayuda a establecer medidas para limitar el
        esparcimiento del VIH.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Contexto',
};
</script>

<style lang="scss" scoped>
.video-responsivo {
  border-radius: 10px;
  border: 1px solid #333;
  overflow: hidden;
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
